// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Terms of Service Page Route
 *
 * Public terms of service page for Password Manager.
 * Accessible without authentication.
 * International multi-jurisdictional approach with SaaS-specific clauses.
 *
 * @module routes/terms
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { config } from "@/lib/config";
import { Header, Footer } from "@/components/landing";
import { LegalPageLayout, MarkdownLegalDocument } from "@/components/legal";
import { LegalService } from "@/services/legal";

export const Route = createFileRoute("/terms")({
  component: TermsOfServicePage,
});

function formatDate(value: string | Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(
    new Date(value),
  );
}

function TermsOfServicePage() {
  const { t, i18n } = useTranslation(["legal", "common"]);
  const siteUrl = config.siteUrl;
  const { data, isLoading, error } = useQuery({
    queryKey: ["legal", "terms-current", i18n.language],
    queryFn: LegalService.getTermsCurrent,
  });

  return (
    <>
      <Helmet>
        <title>{t("terms.meta.title")} | {t("common:app.name")}</title>
        <meta name="description" content={data?.summary ?? t("terms.meta.description")} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${siteUrl}/terms`} />
      </Helmet>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground"
      >
        {t("common:a11y.skipToContent", "Skip to main content")}
      </a>

      {/* Page Structure */}
      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main id="main-content" role="main">
          {isLoading ? (
            <div className="flex min-h-[50vh] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error || !data ? (
            <div className="container mx-auto px-4 py-16 text-center text-muted-foreground">
              {t("page.loadError")}
            </div>
          ) : (
            <LegalPageLayout
              title={t("terms.title")}
              lastUpdated={formatDate(data.effectiveFrom, i18n.language)}
              effectiveDate={formatDate(data.effectiveFrom, i18n.language)}
            >
              <p className="mb-6 text-sm text-muted-foreground">
                {t("common.version")}: {data.version}
              </p>
              <MarkdownLegalDocument markdown={data.body} />
            </LegalPageLayout>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}
